'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from '@/app/hooks/useInView';
import { SPARTAN_HOME_EVENT_NAME } from '@/app/lcv/components/sections/spartan/home/shared/tracking';
import type { SpartanHomeSpecialOfferCarouselProps } from '@/app/lcv/components/sections/spartan/home/special-offer/carousel/types';
import { SpartanHomeSpecialOfferCard } from '@/app/lcv/components/sections/spartan/home/special-offer/offer-card';
import { SPARTAN_HOME_SPECIAL_OFFER_DATA_GA, SPARTAN_HOME_SPECIAL_OFFER_TRACKING } from '@/app/lcv/components/sections/spartan/home/special-offer/tracking';
import { SPARTAN_MEDIA_DEFAULT_IMAGE_DURATION_MS } from '@/app/lcv/components/sections/spartan/shared/hooks/mediaProgress';
import { FADE_CAROUSEL_LIST_CLASS_NAME, getFadeCarouselSlideClassName, useFadeCarousel } from '@/app/lcv/components/sections/spartan/shared/hooks/useFadeCarousel';
import { useMediaProgress } from '@/app/lcv/components/sections/spartan/shared/hooks/useMediaProgress';
import { usePageActivity } from '@/app/lcv/components/sections/spartan/shared/hooks/usePageActivity';
import { SpartanSlidePagination } from '@/app/lcv/components/sections/spartan/shared/slide-pagination';
import type { SpartanSlidePaginationHandle } from '@/app/lcv/components/sections/spartan/shared/slide-pagination/types';
import { SPARTAN_SLIDER_NAV_LABELS } from '@/app/lcv/components/sections/spartan/shared/slider-nav/constants';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
export const SpartanHomeSpecialOfferCarousel = t0 => {
  const $ = _c(38);
  const {
    items: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const items = t2;
  const {
    selectedIndex,
    currentPage,
    onPageChange,
    goToNext,
    swipeHandlers
  } = useFadeCarousel(items.length);
  const paginationRef = useRef(null);
  const wrapperRef = useRef(null);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      threshold: 0.35,
      once: false,
      initialState: false
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const inView = useInView(wrapperRef, t3);
  const isPageActive = usePageActivity();
  const canAutoplay = inView && isPageActive;
  const canAutoplayRef = useRef(canAutoplay);
  let t4;
  let t5;
  if ($[3] !== canAutoplay) {
    t4 = () => {
      canAutoplayRef.current = canAutoplay;
    };
    t5 = [canAutoplay];
    $[3] = canAutoplay;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = progress => {
      paginationRef.current?.updateProgress(progress);
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const updateProgressVisual = t6;
  let t7;
  if ($[7] !== goToNext) {
    t7 = {
      durationMs: SPARTAN_MEDIA_DEFAULT_IMAGE_DURATION_MS,
      onProgress: updateProgressVisual,
      onComplete: goToNext
    };
    $[7] = goToNext;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const {
    start,
    pause,
    reset
  } = useMediaProgress(t7);
  let t8;
  if ($[9] !== reset || $[10] !== start) {
    t8 = () => {
      reset();
      if (!canAutoplayRef.current) {
        return;
      }
      start();
    };
    $[9] = reset;
    $[10] = start;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== reset || $[13] !== selectedIndex || $[14] !== start) {
    t9 = [selectedIndex, reset, start];
    $[12] = reset;
    $[13] = selectedIndex;
    $[14] = start;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[16] !== canAutoplay || $[17] !== pause || $[18] !== start) {
    t10 = () => {
      if (!canAutoplay) {
        pause();
        return;
      }
      start();
    };
    t11 = [canAutoplay, pause, start];
    $[16] = canAutoplay;
    $[17] = pause;
    $[18] = start;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = createDataAttributes({
      test: SPARTAN_HOME_SPECIAL_OFFER_TRACKING.itemList
    });
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== items || $[23] !== selectedIndex) {
    let t14;
    if ($[25] !== items.length || $[26] !== selectedIndex) {
      t14 = (item, index) => <li key={[item.title, index].join("-")} className={getFadeCarouselSlideClassName(index === selectedIndex)} inert={index !== selectedIndex} aria-roledescription="slide" aria-label={`${index + 1} / ${items.length}`} {...createDataAttributes({
        test: SPARTAN_HOME_SPECIAL_OFFER_TRACKING.item(index)
      })}><SpartanHomeSpecialOfferCard item={item} index={index} isActive={index === selectedIndex} /></li>;
      $[25] = items.length;
      $[26] = selectedIndex;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    t13 = items.map(t14);
    $[22] = items;
    $[23] = selectedIndex;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[28] !== swipeHandlers || $[29] !== t13) {
    t14 = <ul className={FADE_CAROUSEL_LIST_CLASS_NAME} {...swipeHandlers} {...t12}>{t13}</ul>;
    $[28] = swipeHandlers;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== currentPage || $[32] !== items.length || $[33] !== onPageChange) {
    t15 = items.length > 1 && <SpartanSlidePagination ref={paginationRef} testId={SPARTAN_HOME_SPECIAL_OFFER_TRACKING.pagination} itemTestIdPrefix={SPARTAN_HOME_SPECIAL_OFFER_TRACKING.paginationItem} theme="dark" currentPage={currentPage} totalPages={items.length} progress={0} onPageChange={onPageChange} wrapperClassName="mt-6 justify-center md:mt-8" tracking={{
      eventName: SPARTAN_HOME_EVENT_NAME,
      group: SPARTAN_HOME_SPECIAL_OFFER_DATA_GA.section
    }} />;
    $[31] = currentPage;
    $[32] = items.length;
    $[33] = onPageChange;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t14 || $[36] !== t15) {
    t16 = <div ref={wrapperRef} role="region" aria-roledescription="carousel" aria-label={SPARTAN_SLIDER_NAV_LABELS.carousel}>{t14}{t15}</div>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
};